// Types
import type {
  TAutomationStatus,
  TAutomatedEmailSaleType,
  TAutomatedEmailUserSettingsResponse,
  TAutomatedEmailUserSettingsUpdatePayload,
} from '~/modules/opportunities/automated-email/types';
import type {
  TSemiAutomaticUiDays,
  TApiSendDelayHours,
} from '~/modules/opportunities/automated-email/settings/types';
import { EAutomationMode } from '~/modules/opportunities/automated-email/types';

// Config
import { DEFAULT_SEMI_AUTOMATIC_AUTO_SEND_DAYS, DEFAULT_UNCONFIGURED_CONTACT_FREQUENCY } from '~/modules/opportunities/automated-email/settings/config';

export function getUnconfiguredAutomationStatus(): TAutomationStatus {
  return {
    mode: EAutomationMode.OFF,
    is_enabled: false,
    contact_frequency: DEFAULT_UNCONFIGURED_CONTACT_FREQUENCY,
    semi_automatic_auto_send_days: DEFAULT_SEMI_AUTOMATIC_AUTO_SEND_DAYS,
    sender_user_id: null,
    has_changed_today: false,
  };
}

function sendDelayHoursToSemiDays(
  hours: TAutomatedEmailUserSettingsResponse['send_delay_hours']
): TSemiAutomaticUiDays {
  if (hours === 24) {
    return 1;
  }

  if (hours === 48) {
    return 2;
  }

  if (hours === 72) {
    return 3;
  }

  return 0;
}

export function semiAutomaticSendDaysToSendDelayHours(
  days: number
): TApiSendDelayHours | null {
  if (days <= 0) {
    return null;
  }

  if (days === 1) {
    return 24;
  }

  if (days === 2) {
    return 48;
  }

  if (days === 3) {
    return 72;
  }

  return 24;
}

/** True when `updated_at` is a valid timestamp on the user's local calendar day. */
export function isUserSettingsUpdatedToday(updatedAt: string | null | undefined): boolean {
  if (!updatedAt?.trim()) {
    return false;
  }

  const parsed: Date = new Date(updatedAt);

  if (Number.isNaN(parsed.getTime())) {
    return false;
  }

  const now: Date = new Date();

  return (
    parsed.getFullYear() === now.getFullYear() &&
    parsed.getMonth() === now.getMonth() &&
    parsed.getDate() === now.getDate()
  );
}

function resolveAutomationModeFromApiResponse(
  api: TAutomatedEmailUserSettingsResponse
): EAutomationMode {
  if (!api.is_enabled) {
    return EAutomationMode.OFF;
  }

  if (api.mode === EAutomationMode.FULLY_AUTOMATIC) {
    return EAutomationMode.FULLY_AUTOMATIC;
  }

  return EAutomationMode.SEMI_AUTOMATIC;
}

export function mapUserSettingsResponseToAutomationStatus(
  api: TAutomatedEmailUserSettingsResponse
): TAutomationStatus {
  const mode: EAutomationMode = resolveAutomationModeFromApiResponse(api);

  return {
    mode,
    is_enabled: api.is_enabled,
    contact_frequency: api.contact_frequency,
    semi_automatic_auto_send_days: sendDelayHoursToSemiDays(api.send_delay_hours),
    sender_user_id: api.sender_user_id,
    has_changed_today: isUserSettingsUpdatedToday(api.updated_at),
  };
}

function resolveStoredAutomationModeForPayload(
  mode: EAutomationMode,
  savedAutomationMode: EAutomationMode
): EAutomationMode {
  const isOff: boolean = mode === EAutomationMode.OFF;

  if (isOff && savedAutomationMode !== EAutomationMode.OFF) {
    return savedAutomationMode;
  }

  if (isOff) {
    return EAutomationMode.SEMI_AUTOMATIC;
  }

  return mode;
}

function resolveSendDelayHoursForPayload(
  mode: EAutomationMode,
  isOff: boolean,
  semiAutomaticAutoSendDays: number
): TAutomatedEmailUserSettingsUpdatePayload['send_delay_hours'] {
  if (isOff || mode !== EAutomationMode.SEMI_AUTOMATIC) {
    return null;
  }

  return semiAutomaticSendDaysToSendDelayHours(semiAutomaticAutoSendDays);
}

export function buildUserSettingsUpdatePayload(
  patchId: number,
  saleType: TAutomatedEmailSaleType,
  mode: EAutomationMode,
  savedAutomationMode: EAutomationMode,
  contactFrequency: number,
  semiAutomaticAutoSendDays: number,
  senderUserId: number
): TAutomatedEmailUserSettingsUpdatePayload {
  const isOff: boolean = mode === EAutomationMode.OFF;
  const storedAutomationMode: EAutomationMode = resolveStoredAutomationModeForPayload(
    mode,
    savedAutomationMode
  );

  const payload: TAutomatedEmailUserSettingsUpdatePayload = {
    patch_id: patchId,
    sale_type: saleType,
    sender_user_id: senderUserId,
    is_enabled: !isOff,
    mode: storedAutomationMode,
    contact_frequency: contactFrequency,
    send_delay_hours: resolveSendDelayHoursForPayload(mode, isOff, semiAutomaticAutoSendDays),
  };

  return payload;
}

export type { TSemiAutomaticUiDays, TApiSendDelayHours };
